/** A single row of the printed student list. */
export interface StudentListRow {
    studentName: string;
    fatherName?: string | null;
    motherName?: string | null;
    /* Not columns on this sheet — the class prints in the header instead. They stay on
       the row because the same rows are handed to the name slips, which do show them. */
    classSection?: string | null;
    rollNumber?: string | null;
    /** ISO date (`YYYY-MM-DD`) as the API sends it — formatted for print, not pre-formatted. */
    dateOfBirth?: string | null;
    aadhaarNumber?: string | null;
    fatherContact?: string | null;
    address?: string | null;
    transportRoute?: string | null;
}

export interface StudentListPrintData {
    schoolName?: string;
    /** The class the list was filtered to. Falls back to "All Classes" when unset. */
    className?: string | null;
    /** The section, when one was picked. Omitted from the header entirely when absent. */
    sectionName?: string | null;
    /** The name the list was searched by, when one was applied. */
    searchTerm?: string | null;
    rows: StudentListRow[];
}

/** Row values are interpolated into markup, so they must be escaped. */
function esc(v: string | number | null | undefined): string {
    return String(v ?? '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
}

/** Missing values print as an em dash rather than an empty cell. */
function cell(v: string | null | undefined): string {
    const s = (v ?? '').trim();
    return s.length > 0 ? esc(s) : '<span class="muted">&mdash;</span>';
}

const clean = (v: string | null | undefined): string | null => {
    const s = (v ?? '').trim();
    return s.length > 0 ? s : null;
};

/** `2015-03-12` prints as `12 Mar 2015`. An unparseable value is printed as-is rather
 *  than as "Invalid Date". */
function fmtDob(v: string | null | undefined): string | null {
    const s = clean(v);
    if (!s) return null;
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

/** Builds the print document. Split out from `printStudentList` so the markup can
 *  be rendered and inspected without a browser. */
export function buildStudentListHtml(data: StudentListPrintData): string {
    const printedOn = new Date().toLocaleDateString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
    });

    const rowsHtml = data.rows.map((r, i) => `
      <tr>
        <td class="num">${i + 1}</td>
        <td class="name">${cell(r.studentName)}</td>
        <td>${cell(r.fatherName)}</td>
        <td>${cell(r.motherName)}</td>
        <td class="num">${cell(fmtDob(r.dateOfBirth))}</td>
        <td class="num">${cell(r.aadhaarNumber)}</td>
        <td class="num">${cell(r.fatherContact)}</td>
        <td class="address">${cell(r.address)}</td>
        <td>${cell(r.transportRoute)}</td>
      </tr>`).join('');

    const section = clean(data.sectionName);
    const search = clean(data.searchTerm);

    /* Class always prints so the sheet is self-describing once it leaves the screen.
       Section and Search only appear when they were actually applied. */
    const field = (label: string, value: string) => `
      <div class="field">
        <span class="field__label">${esc(label)}</span>
        <span class="field__value">${esc(value)}</span>
      </div>`;

    const fieldsHtml = [
        field('Class', clean(data.className) ?? 'All Classes'),
        section ? field('Section', section) : '',
        search ? field('Search', `"${search}"`) : '',
        field('Students', String(data.rows.length)),
        field('Printed', printedOn),
    ].join('');

    const html = `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>Student List</title>
<style>
  /* A4 landscape: 297mm x 210mm. With 10mm side margins the sheet has 277mm of
     usable width, which the table fills exactly (column widths total 100%). */
  @page { size: A4 landscape; margin: 10mm 10mm 12mm; }

  * { box-sizing: border-box; }
  html, body { margin: 0; padding: 0; }
  body {
    font-family: system-ui, -apple-system, "Segoe UI", Roboto, sans-serif;
    color: #0f172a;
    font-size: 9.5px;
    line-height: 1.35;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  /* ---- Header ---- */
  .header {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: 16px;
    padding-bottom: 6px;
    margin-bottom: 8px;
    border-bottom: 2px solid #0f172a;
  }
  .brand h1 {
    margin: 0;
    font-size: 16px;
    font-weight: 800;
    letter-spacing: -0.01em;
  }
  .brand .doc {
    margin: 1px 0 0;
    font-size: 9px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #64748b;
  }

  /* Selected class / section read as labelled fields, not a run-on subtitle. */
  .fields { display: flex; align-items: flex-end; gap: 14px; }
  .field { display: flex; flex-direction: column; gap: 1px; }
  .field__label {
    font-size: 6.5px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    color: #94a3b8;
  }
  .field__value {
    font-size: 10px;
    font-weight: 700;
    color: #0f172a;
    white-space: nowrap;
  }

  /* ---- Table ---- */
  table { width: 100%; border-collapse: collapse; table-layout: fixed; }

  /* Repeat the column headings at the top of every printed page, and never let a
     row straddle a page break. */
  thead { display: table-header-group; }
  tr { page-break-inside: avoid; break-inside: avoid; }

  th {
    text-align: left;
    font-size: 7px;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: #475569;
    background: #f1f5f9;
    padding: 4px 5px;
    border-bottom: 1px solid #cbd5e1;
  }
  td {
    padding: 4px 5px;
    border-bottom: 1px solid #e8edf3;
    vertical-align: top;
    /* Long unbroken values (addresses, emails) wrap instead of widening the column. */
    overflow-wrap: anywhere;
  }
  tbody tr:nth-child(even) td { background: #f8fafc; }

  .name { font-weight: 600; }
  .num { font-variant-numeric: tabular-nums; }
  .address { color: #334155; }
  .muted { color: #94a3b8; }

  /* Widths total 100%. Sized to actual content: the fixed-width fields (serial, DOB,
     Aadhaar, contact) are pared back to what they hold, and the slack goes to the names
     and Address, which are the only columns long enough to need it. */
  .c-sno { width: 3%; }
  .c-name { width: 14%; }
  .c-father { width: 13%; }
  .c-mother { width: 13%; }
  .c-dob { width: 7%; }
  .c-aadhaar { width: 9%; }
  .c-contact { width: 8%; }
  .c-address { width: 21%; }
  .c-route { width: 12%; }

  .footer {
    margin-top: 8px;
    padding-top: 5px;
    border-top: 1px dashed #cbd5e1;
    font-size: 7.5px;
    color: #94a3b8;
    display: flex;
    justify-content: space-between;
  }
</style>
</head>
<body>
  <div class="header">
    <div class="brand">
      <h1>${esc(data.schoolName || 'Gurukul Academy')}</h1>
      <p class="doc">Student List</p>
    </div>
    <div class="fields">${fieldsHtml}</div>
  </div>

  <table>
    <thead>
      <tr>
        <th class="c-sno">#</th>
        <th class="c-name">Student Name</th>
        <th class="c-father">Father's Name</th>
        <th class="c-mother">Mother's Name</th>
        <th class="c-dob">Date of Birth</th>
        <th class="c-aadhaar">Aadhaar No.</th>
        <th class="c-contact">Father's Contact</th>
        <th class="c-address">Address</th>
        <th class="c-route">Transport Route</th>
      </tr>
    </thead>
    <tbody>
      ${rowsHtml}
    </tbody>
  </table>

  <div class="footer">
    <span>${esc(data.schoolName || 'Gurukul Academy')} &middot; Printed on ${esc(printedOn)}</span>
    <span>${data.rows.length} record${data.rows.length === 1 ? '' : 's'}</span>
  </div>
<script>
  window.onload = function () { window.print(); };
<${'/'}script>
</body>
</html>`;

    return html;
}

export function printStudentList(data: StudentListPrintData) {
    const w = window.open('', '_blank', 'width=1100,height=800');
    if (w) {
        w.document.write(buildStudentListHtml(data));
        w.document.close();
    }
}
